import * as React from 'react'
import * as Redux from 'react-redux'
import styled, { css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import { t, tc } from '@owl-nest/localize'
import * as date from '@owl-nest/date'
import * as api from '@owl-nest/api-client/latest'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'
import * as model from '@owl-nest/models'

import * as styles from '../style'

import * as modalAction from '../../../../actions/modal'
import { Supporter } from '../../../models/supporter'
import * as projectModel from '../../../models/project'
import { formatBacker } from '../utils/formatBacker'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../utils/trackerEnums'
import * as URLs from '../../../utils/URLs'

type BackerPropTypes = {
  supporter: api.Supporter
  project: projectModel.Project
  variant?: string
  source: TRACKING_SOURCE
}

export function Backer({ supporter, project, variant, source }: BackerPropTypes): React.ReactElement<BackerPropTypes> {
  const { dateOrder, labelColor, labelName } = formatBacker(supporter, project)

  const isOwnerComment = supporter.id === project.owner.id

  const hasComment = !isOwnerComment && supporter.latest_project_comment && supporter.latest_project_comment?.is_public

  return (
    <styles.BackerContainer $variant={variant}>
      {/* Avatar */}
      <styles.Avatar href={supporter.absolute_url} $variant={variant}>
        <plume.styles.image.Avatar src={model.user.avatar(supporter, '55x55')} type="medium" />
      </styles.Avatar>
      <BackerInfo $variant={variant}>
        <CopyWrapper hasComment={hasComment}>
          <div>
            <Copy>
              {/* Name */}
              {supporter.is_anonymous ? (
                <plume.styles.heading.XS as="span">{t('Anonymous backer')}</plume.styles.heading.XS>
              ) : (
                <plume.styles.heading.XS as="a" href={supporter.absolute_url}>
                  {model.user.fullName(supporter)}
                </plume.styles.heading.XS>
              )}
              {/* Label */}
              {labelName && (
                <plume.RoleTag forwardedAs="span" backgroundColor={labelColor}>
                  {labelName}
                </plume.RoleTag>
              )}
            </Copy>
            <plume.styles.copy.M.light as="span">
              {project.type === api.ProjectType.Donation ? (
                <DonationBasedContent project={project} supporter={supporter} />
              ) : (
                <RewardBasedContent project={project} source={source} supporter={supporter} />
              )}
            </plume.styles.copy.M.light>
          </div>
          {/* Comments */}
          {hasComment && <CommentWrapper>{supporter.latest_project_comment?.comment}</CommentWrapper>}
        </CopyWrapper>
        {/* Last contribution date */}
        {dateOrder && (
          <DateInfo hasComment={hasComment}>{date.formatDistanceToNow(dateOrder, { includeSeconds: true })}</DateInfo>
        )}
      </BackerInfo>
    </styles.BackerContainer>
  )
}
export function DonationBasedContent({ supporter, project }: Omit<BackerPropTypes, 'source'>) {
  const { supporterLatestOrder } = formatBacker(supporter, project)

  return (
    <>
      {supporterLatestOrder?.type === 'monthly'
        ? tc('made a [Link: monthly donation of %(price)s]', {
            Link: (
              <plume.Link
                tinted
                href={
                  model.project.isOrdersEnabled<projectModel.ExtraFields>(project)
                    ? URLs.getCheckoutURL(project.id)
                    : ''
                }
              />
            ),
            price: plume.accounting.localeMoney(supporterLatestOrder?.order_total, {
              currency: project.currency,
              lang: project.lang,
            }),
          })
        : tc('made a [Link: donation of %(price)s]', {
            Link: (
              <plume.Link
                tinted
                href={
                  model.project.isOrdersEnabled<projectModel.ExtraFields>(project)
                    ? URLs.getCheckoutURL(project.id)
                    : ''
                }
              />
            ),
            price: plume.accounting.localeMoney(supporterLatestOrder?.order_total, {
              currency: project.currency,
              lang: project.lang,
            }),
          })}
    </>
  )
}

export function RewardBasedContent({ supporter, project, source }: BackerPropTypes) {
  const dispatch = Redux.useDispatch()

  const tracking = useTracking()

  const postCampaignLink = project?.post_campaign_link

  const { isLegacy, isHidden, hasTip, firstReward, supporterLatestOrder } = formatBacker(supporter, project)

  function rewardContent() {
    if (firstReward) {
      if (isHidden) {
        return t('supports this project')
      }

      if (isLegacy) {
        if (hasTip && supporterLatestOrder?.tip) {
          return tc('supports this project with [price]', {
            price: (
              <plume.LocaleMoneyDisplayer
                amount={supporterLatestOrder?.tip + firstReward.price}
                currency={project.currency}
                localeFormat={project.lang}
              />
            ),
          })
        }
        return tc('supports this project with [price]', {
          price: (
            <plume.LocaleMoneyDisplayer
              amount={firstReward.price}
              currency={project.currency}
              localeFormat={project.lang}
            />
          ),
        })
      }

      if (supporterLatestOrder?.items && supporterLatestOrder?.items.length > 1) {
        return t('chose %(nb)s rewards', { nb: supporterLatestOrder.items.length })
      }

      return tc('chose the reward [link: %(reward)s]', {
        link: (
          <plume.Link
            tinted
            onClick={() => {
              if (model.project.isFinished<projectModel.ExtraFields>(project) && postCampaignLink !== undefined) {
                dispatch(modalAction.toggleModalPostCampaign(true))
              } else {
                tracking.track({
                  event: EVENT_TYPE.GENERIC,
                  eventCategory: CATEGORY.PROJECT_PAGE,
                  eventAction: TRACKING_ACTION.DISPLAY_REWARD,
                  eventLabel: source,
                })
                dispatch(modalAction.toggleModalReward(true, { id: firstReward.id.toString() }))
              }
            }}
          />
        ),
        reward: model.reward.title(firstReward),
      })
    }

    if (!hasTip) {
      return t('supported the project with a donation')
    }

    return <></>
  }

  return (
    <>
      {rewardContent()}
      {/* Free contribution */}
      {hasTip &&
        !isLegacy &&
        !isHidden &&
        supporterLatestOrder &&
        firstReward &&
        t(' and supported the project with a donation')}
      {hasTip &&
        !isLegacy &&
        !isHidden &&
        supporterLatestOrder &&
        !firstReward &&
        t('supported the project with a donation')}
    </>
  )
}

const BackerInfo = styled(styles.BackerInfo)`
  ${plume.styles.copy.M.light} {
    margin-bottom: 0;
  }

  ${plume.styles.copy.M.light}, ${plume.Link} {
    display: inline;
  }
`

const CopyWrapper = styled.div<{ hasComment?: boolean }>`
  margin-right: 5px;
  display: flex;

  ${({ hasComment }) => {
    return hasComment
      ? css`
          flex-wrap: wrap;
          flex-direction: column;
        `
      : css`
          align-items: center;
        `
  }}
`

const Copy = styled.p`
  margin: 0 0 5px 0;
  display: inline-flex;
  align-items: end;
`

const CommentWrapper = styled(styles.BackerCommentWrapper)``

const DateInfo = styled(plume.styles.heading.XXXXXXS)<{ hasComment?: boolean }>`
  color: ${plume.COLORS.TEXT_SECONDARY};
  text-transform: uppercase;

  ${({ hasComment }) => {
    return (
      !hasComment &&
      css`
        @media screen and ${plume.BREAKPOINTS.MOBILE_L} {
          transform: translateY(11px);
        }
      `
    )
  }}
`
